import { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useQuery } from 'urql'

import AnimatedHeading from '@/Components/UI/AnimatedHeading'
import MetaTitle from '@/Components/UI/MetaTitle'
import PseudoHeader from '@/Components/PseudoHeader/PseudoHeader'
import ConciergeAIInfoBox from '@/Components/Recommendations/ConciergeAIInfoBox'
import HowItWorksDialog from '@/Components/Recommendations/HowItWorksDialog'
import ActionBar from '@/Components/UI/ActionBar'
import { type LocationSelectionData } from '@/Components/PreferredLocation/LocationSection'
import { type EventTypeAttributes } from '@/Views/FormSteps/EventType/Base/Fieldset'
import TimingsSection from '@/Components/DuplicateForm/DateAndTime/TimingSection'
import useBriefContext from '@/Hooks/Brief/useBriefContext'
import { type PreviousEventRequest } from '@/Components/RepeatEvent/PreviousEventCard'
import { type DateRange } from '@/Views/FormSteps/DateTime/Utilities/getBriefDates'
import { type TimeOptionInput } from '@/Views/FormSteps/DateTime/TimePicker/TimeStamps/timeStamps'
import { dateToReadableString } from '@/Views/FormSteps/DateTime/Utilities/dateStrings'
import { loadDuplicateFormDraft, saveDuplicateFormDraft } from '@/Utils/duplicateFormDraft'
import { loadNewBriefDraft, saveNewBriefDraft } from '@/Utils/newBriefDraft'

import EventNameSection from '@/Components/DuplicateForm/EventNameSection'
import useEventNameSuggestions from '@/Hooks/Brief/useEventNameSuggestions'
import EventTypeSection from '@/Components/DuplicateForm/EventTypeSection'
import GuestCountInput from '@/Components/DuplicateForm/GuestCountInput'
import StyleSection from '@/Components/DuplicateForm/StyleSection'
import LayoutSection from '@/Components/DuplicateForm/LayoutSection'
import FacilitiesSection from '@/Components/DuplicateForm/FacilitiesSection'
import BudgetInput from '@/Components/DuplicateForm/BudgetInput'
import AdditionalNotesSection from '@/Components/DuplicateForm/AdditionalNotesSection'
import RequestedVenuesSidebar from '@/Components/DuplicateForm/RequestedVenuesSidebar'
import { useEventTypesQuery } from '@/Views/FormSteps/EventType/Base/Fieldset/Fieldset.generated'
import { StylesDocument, type StylesQuery } from '@/Views/FormSteps/Styles/StylesInput'

interface DuplicateFormLocationState {
  event?: {
    id?: string
    eventName?: string
    currency?: string
    capacity?: string
    requests?: PreviousEventRequest[]
    layoutId?: string
    cateringNeeds?: string
    additionalNotes?: string
    isFlexibleWithDates?: boolean
    flexibilityNotes?: string
    eventTypeId?: string
    styleIds?: string[]
    facilityIds?: number[]
    location?: string
    lat?: number
    lon?: number
    country?: string
    radiusKm?: number
    locationPolygon?: string
  }
}

const formatDateRange = (dates: DateRange): string => {
  const [start, end] = dates
  if (!start) return ''
  if (!end || start.toDateString() === end.toDateString()) {
    return dateToReadableString(start)
  }
  return `${dateToReadableString(start)} – ${dateToReadableString(end)}`
}

const formatTime = (start: TimeOptionInput, end: TimeOptionInput): string => {
  if (!start || !end) return ''
  return `${start} – ${end}`
}

const DuplicateFormPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { brief, dispatchBrief } = useBriefContext()
  const state = (location.state ?? {}) as DuplicateFormLocationState
  const event = state.event ?? {}

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' })
  }, [])

  const isNewBrief = !event.id
  const draft = isNewBrief ? loadNewBriefDraft() : loadDuplicateFormDraft(event.id)

  const [showHowItWorks, setShowHowItWorks] = useState(false)
  const [validationErrors, setValidationErrors] = useState({
    eventName: false,
    budget: false,
    date: false,
    time: false,
  })

  const [eventName, setEventName] = useState(draft?.eventName ?? '')
  const [numGuests, setNumGuests] = useState<number | ''>(
    draft?.numGuests !== undefined
      ? draft.numGuests
      : event.capacity
      ? parseInt(event.capacity, 10)
      : ''
  )
  const [eventTypeAttrs, setEventTypeAttrs] = useState<EventTypeAttributes>(
    draft?.eventTypeAttrs ?? { collection: undefined, collectionName: undefined }
  )
  const [styleIds, setStyleIds] = useState<string[]>(draft?.styleIds ?? event.styleIds ?? [])
  const [layout, setLayout] = useState<string | undefined>(draft?.layout ?? event.layoutId)
  const [facilityIds, setFacilityIds] = useState<number[]>(
    draft?.facilityIds ?? event.facilityIds ?? []
  )
  const [cateringNeeds, setCateringNeeds] = useState(
    draft?.cateringNeeds ?? event.cateringNeeds ?? ''
  )
  const [budget, setBudget] = useState<number | ''>(draft?.budget !== undefined ? draft.budget : '')
  const [additionalNotes, setAdditionalNotes] = useState(
    draft?.additionalNotes ?? event.additionalNotes ?? ''
  )

  const [requestedVenues, setRequestedVenues] = useState<PreviousEventRequest[]>(
    draft?.requestedVenues ?? event.requests ?? []
  )
  const locationData = useMemo<LocationSelectionData | null>(() => {
    if (event.lat == null || event.lon == null) return null
    const parsedPolygon = (() => {
      try {
        return event.locationPolygon ? JSON.parse(event.locationPolygon) : null
      } catch {
        return null
      }
    })()
    return {
      location: event.location ?? '',
      lat: event.lat,
      lon: event.lon,
      country: event.country ?? '',
      ...(parsedPolygon?.length
        ? {
            mode: 'polygon' as const,
            radiusKm: event.radiusKm ?? 3,
            polygonCoordinates: parsedPolygon,
          }
        : { mode: 'radius' as const, radiusKm: event.radiusKm ?? 3 }),
    }
  }, [])

  const [eventTypeCollection, setEventTypeCollection] = useState<string | undefined>(
    draft?.eventTypeAttrs?.collection ?? undefined
  )
  const [dates, setDates] = useState<DateRange>(() => {
    if (draft?.dateStart) {
      return [new Date(draft.dateStart), draft.dateEnd ? new Date(draft.dateEnd) : undefined]
    }
    return [undefined, undefined]
  })
  const [startTime, setStartTime] = useState<TimeOptionInput>(draft?.startTime ?? '')
  const [endTime, setEndTime] = useState<TimeOptionInput>(draft?.endTime ?? '')
  const [isFlexibleOnDate, setIsFlexibleOnDate] = useState(
    draft?.isFlexibleOnDate ?? event.isFlexibleWithDates ?? false
  )
  const [isFlexibleOnTime, setIsFlexibleOnTime] = useState(draft?.isFlexibleOnTime ?? false)
  const [flexibilityNotes, setFlexibilityNotes] = useState(
    draft?.flexibilityNotes ?? event.flexibilityNotes ?? ''
  )

  const [{ data: eventTypesData, fetching: fetchingEventTypes }] = useEventTypesQuery({
    requestPolicy: 'cache-first',
  })
  const [{ data: stylesData, fetching: fetchingStyles }] = useQuery<StylesQuery>({
    query: StylesDocument,
    context: useMemo(() => ({ requestPolicy: 'cache-first' }), []),
  })

  const eventTypeName = event.eventTypeId
    ? (eventTypesData?.BBBE_collections ?? []).find(
        (c) => c.id === event.eventTypeId || c.id === String(Number(event.eventTypeId))
      )?.name
    : undefined
  const styleNames = event.styleIds?.length
    ? event.styleIds
        .map((id) => stylesData?.getStyles.find((s) => s.id === id)?.label ?? '')
        .filter(Boolean)
    : undefined
  const layoutName =
    event.layoutId && event.layoutId !== 'not_sure' ? event.layoutId.replace(/_/g, ' ') : undefined

  const { suggestions: nameSuggestions, loading: loadingNameSuggestions } = useEventNameSuggestions(
    {
      eventName: event.eventName,
      eventTypeName,
      layoutName,
      styleNames,
      cateringNeeds: event.cateringNeeds,
      additionalNotes: event.additionalNotes,
    },
    { pause: fetchingEventTypes || fetchingStyles }
  )

  useEffect(() => {
    const timer = setTimeout(() => {
      const saveDraft = isNewBrief ? saveNewBriefDraft : saveDuplicateFormDraft
      saveDraft({
        eventId: event.id,
        eventName,
        numGuests,
        eventTypeAttrs,
        styleIds,
        layout,
        facilityIds,
        cateringNeeds,
        budget,
        additionalNotes,
        dateStart: dates[0]?.toISOString() ?? null,
        dateEnd: dates[1]?.toISOString() ?? null,
        startTime,
        endTime,
        isFlexibleOnDate,
        isFlexibleOnTime,
        flexibilityNotes,
        requestedVenues,
        initialLocationData: locationData,
      })
    }, 500)
    return () => clearTimeout(timer)
  }, [
    eventName,
    numGuests,
    eventTypeAttrs,
    styleIds,
    layout,
    facilityIds,
    cateringNeeds,
    budget,
    additionalNotes,
    dates,
    startTime,
    endTime,
    isFlexibleOnDate,
    isFlexibleOnTime,
    flexibilityNotes,
    requestedVenues,
    locationData,
  ])

  const eventNameSectionRef = useRef<HTMLDivElement>(null)
  const timingsSectionRef = useRef<HTMLDivElement>(null)
  const budgetSectionRef = useRef<HTMLDivElement>(null)

  const onDetailsContinue = () => {
    const eventNameValid = eventName.trim() !== ''
    const dateValid = dates[0] != null
    const timeValid = startTime !== '' && endTime !== ''
    const budgetValid = budget !== ''

    if (!eventNameValid || !budgetValid || !dateValid || !timeValid) {
      setValidationErrors({
        eventName: !eventNameValid,
        budget: !budgetValid,
        date: !dateValid,
        time: !timeValid,
      })

      const firstErrorRef = !eventNameValid
        ? eventNameSectionRef
        : !dateValid || !timeValid
        ? timingsSectionRef
        : budgetSectionRef
      firstErrorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }

    setValidationErrors({ eventName: false, budget: false, date: false, time: false })

    const saveDraft = isNewBrief ? saveNewBriefDraft : saveDuplicateFormDraft
    saveDraft({
      eventId: event.id,
      eventName,
      numGuests,
      eventTypeAttrs,
      styleIds,
      layout,
      facilityIds,
      cateringNeeds,
      budget,
      additionalNotes,
      dateStart: dates[0]?.toISOString() ?? null,
      dateEnd: dates[1]?.toISOString() ?? null,
      startTime,
      endTime,
      isFlexibleOnDate,
      isFlexibleOnTime,
      flexibilityNotes,
      requestedVenues,
      initialLocationData: locationData,
    })

    dispatchBrief({
      updateAttributes: {
        eventName,
        capacities: numGuests || undefined,
        ...eventTypeAttrs,
        styleIds,
        layouts: layout,
        fromDate: dates[0]?.toISOString(),
        toDate: dates[1]?.toISOString(),
        fromTime: startTime || undefined,
        toTime: endTime || undefined,
        isFlexibleOnDate,
        isFlexibleOnTime,
        flexibleDatesNote: flexibilityNotes || undefined,
        spaceFacilityIds: facilityIds,
        cateringNeeds: cateringNeeds || undefined,
        budget: budget || undefined,
        additionalNotes: additionalNotes || undefined,
      },
    })

    window.scrollTo({ top: 0, behavior: 'smooth' })
    navigate('/preferred-location', {
      state: {
        eventName,
        guests: numGuests,
        dateRange: formatDateRange(dates),
        time: formatTime(startTime, endTime),
        budget,
        additionalNotes: additionalNotes || undefined,
        requestedVenues,
        originalEventName: event.eventName,
        initialLocationData: locationData,
        event,
      },
    })
  }

  return (
    <>
      <MetaTitle title='Finalise your event - HeadBox' />
      <PseudoHeader />

      <div className='mt-16 pt-6 md:pt-10 pb-36 px-4 sm:px-6'>
        <div className='w-full max-w-[1120px] mx-auto flex flex-col lg:flex-row gap-8 items-start py-4 sm:py-0'>
          <div className='flex-1 min-w-0'>
            <AnimatedHeading className='mb-6' size='large'>
              Finalise all your event details:
            </AnimatedHeading>

            <motion.div
              initial={{ opacity: 0, y: -15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1, ease: 'easeOut' }}
              className='flex flex-col gap-4 mb-6'>
              <ConciergeAIInfoBox
                onHowItWorksClick={() => setShowHowItWorks(true)}
                description={
                  'We’ve pre-filled some fields using the venues you chose, and information ' +
                  'from your booking history - just check it’s all correct and fill in the specifics.'
                }
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.25, ease: 'easeOut' }}
              className='bg-white border border-[#f0f0f0] rounded-[4px] px-4 py-6 flex flex-col gap-6'>
              <div ref={eventNameSectionRef}>
                <EventNameSection
                  initialValue={event.eventName}
                  savedValue={draft?.eventName}
                  suggestedNames={nameSuggestions}
                  loadingSuggestions={loadingNameSuggestions}
                  hasError={validationErrors.eventName}
                  onChange={(value) => {
                    setEventName(value)
                    if (value.trim() !== '')
                      setValidationErrors((e) => ({ ...e, eventName: false }))
                  }}
                />
              </div>
              <EventTypeSection
                initialEventTypeId={draft?.eventTypeAttrs?.collection ?? event.eventTypeId}
                onChange={(attrs) => {
                  setEventTypeAttrs(attrs)
                  setEventTypeCollection(attrs.collection ?? undefined)
                }}
              />
              <GuestCountInput
                initialValue={
                  draft?.numGuests !== undefined
                    ? draft.numGuests
                    : event.capacity
                    ? parseInt(event.capacity, 10)
                    : ''
                }
                onChange={setNumGuests}
              />
              <StyleSection
                initialStyleIds={draft?.styleIds ?? event.styleIds}
                onChange={setStyleIds}
              />
              <LayoutSection
                eventTypeCollection={eventTypeCollection}
                initialLayout={draft?.layout ?? event.layoutId}
                onChange={setLayout}
              />
              <div ref={timingsSectionRef}>
                <TimingsSection
                  dates={dates}
                  setDates={(value) => {
                    setDates(value)
                    const next = typeof value === 'function' ? value(dates) : value
                    if (next[0] != null) setValidationErrors((e) => ({ ...e, date: false }))
                  }}
                  startTime={startTime}
                  setStartTime={(value) => {
                    setStartTime(value)
                    const next = typeof value === 'function' ? value(startTime) : value
                    if (next && endTime) setValidationErrors((e) => ({ ...e, time: false }))
                  }}
                  endTime={endTime}
                  setEndTime={(value) => {
                    setEndTime(value)
                    const next = typeof value === 'function' ? value(endTime) : value
                    if (startTime && next) setValidationErrors((e) => ({ ...e, time: false }))
                  }}
                  isFlexibleOnDate={isFlexibleOnDate}
                  setIsFlexibleOnDate={setIsFlexibleOnDate}
                  isFlexibleOnTime={isFlexibleOnTime}
                  setIsFlexibleOnTime={setIsFlexibleOnTime}
                  flexibilityNotes={flexibilityNotes}
                  setFlexibilityNotes={setFlexibilityNotes}
                  dateError={validationErrors.date}
                  timeError={validationErrors.time}
                />
              </div>
              <FacilitiesSection
                eventTypeCollection={eventTypeCollection}
                initialFacilityIds={draft?.facilityIds ?? event.facilityIds}
                initialCateringNeeds={draft?.cateringNeeds ?? event.cateringNeeds}
                onChange={(ids, needs) => {
                  setFacilityIds(ids)
                  setCateringNeeds(needs)
                }}
              />
              <div ref={budgetSectionRef}>
                <BudgetInput
                  initialValue={draft?.budget}
                  hasError={validationErrors.budget}
                  currency={event.currency ?? brief.currency}
                  onChange={(value) => {
                    setBudget(value)
                    if (value !== '') setValidationErrors((e) => ({ ...e, budget: false }))
                  }}
                />
              </div>
              <AdditionalNotesSection
                initialValue={draft?.additionalNotes ?? event.additionalNotes}
                onChange={setAdditionalNotes}
              />
            </motion.div>

            {requestedVenues.length > 0 && (
              <div className='lg:hidden mt-6'>
                <RequestedVenuesSidebar
                  venues={requestedVenues}
                  onRemove={(i) => setRequestedVenues((prev) => prev.filter((_, idx) => idx !== i))}
                />
              </div>
            )}
          </div>

          {requestedVenues.length > 0 && (
            <div className='hidden lg:block w-[340px] shrink-0'>
              <div className='sticky top-[80px]'>
                <RequestedVenuesSidebar
                  venues={requestedVenues}
                  onRemove={(i) => setRequestedVenues((prev) => prev.filter((_, idx) => idx !== i))}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <ActionBar
        primaryLabel='Continue to location'
        onPrimary={onDetailsContinue}
        secondaryLabel='Back'
        onSecondary={() => navigate('/repeat-event', { replace: true })}
        primaryWide
      />

      <HowItWorksDialog
        isOpen={showHowItWorks}
        onClose={() => setShowHowItWorks(false)}
        variant='duplicateForm'
      />
    </>
  )
}

export default DuplicateFormPage
